/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import * as _ from 'lodash';

import template from './target-summary.component.html';
import {ADN_TARGETING_TYPES} from './targeting-constants';
import {TargetingHelper} from './targeting-helper';
import {deferSignalStateWrite} from '../common/util/template-state';

interface ActiveTarget {
  id: string;
  icon: string;
  targets: string;
  targets2?: boolean;
  currentSize?: number;
  matchingGroup?: number;
  results?: boolean;
  /** Cloned slice for template bindings — live targeting arrays can be replaced mid-CD (NG0100). */
  targetData?: any;
  /** Buffered mandatory flag — avoids NG0100 when axProduct targeting fills mid-CD. */
  isMandatory?: boolean;
  [key: string]: any;
}

@Component({
  standalone: false,
  selector: 'adn-target-summary',
  template
})
export class TargetSummaryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() targeting: any;
  @Input() model: any;
  @Input() copyHook: any;
  @Input() type: string;
  @Input() linkTargets = false;

  readonly activeTargets = signal<ActiveTarget[]>([]);

  private readonly targTypes = _.cloneDeep(ADN_TARGETING_TYPES);
  private mandatoryElements: any = {};
  private mandatoryTimeout: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.setupCopyHook();
    this.refreshTargets();
    this.scheduleMandatoryRecalc();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.targeting || changes.model || changes.type) {
      const first = !!(changes.targeting?.firstChange || changes.model?.firstChange || changes.type?.firstChange);
      if (first) {
        this.refreshTargets();
        this.scheduleMandatoryRecalc();
      } else {
        // Defer — targeting/model can still be filling during this CD turn (NG0100).
        deferSignalStateWrite(() => {
          this.refreshTargets();
          this.scheduleMandatoryRecalc();
        });
      }
    }
    if (changes.copyHook) {
      this.setupCopyHook();
    }
  }

  ngOnDestroy(): void {
    if (this.mandatoryTimeout) {
      clearTimeout(this.mandatoryTimeout);
    }
  }

  copyTarget(target: ActiveTarget): void {
    this.copyHook?.copy(target);
  }

  private refreshTargets(): void {
    this.activeTargets.set(_.filter(this.targTypes, (targetConfig: ActiveTarget) => {
      const targetingModel = targetConfig.targets2 ? this.targeting : _.get(this.targeting, [targetConfig.targets]);
      const size = TargetingHelper.getSizeOfTargetingTypes(targetingModel, targetConfig.targets);
      targetConfig.currentSize = size;
      targetConfig.matchingGroup = _.isArray(targetingModel)
        ? _.get(targetingModel, [0, 'matchingGroup'], 0)
        : _.get(targetingModel, ['matchingGroup'], 0);
      targetConfig.isMandatory = !!this.mandatoryElements[targetConfig.id];
      const nextData = targetConfig.targets2 ? this.targeting : targetingModel;
      // Reuse the previous snapshot ref when content is unchanged so CD does not
      // see a new array identity on every refresh.
      if (!_.isEqual(targetConfig.targetData, nextData)) {
        targetConfig.targetData = _.cloneDeep(nextData);
      }
      return size > 0;
    }));
  }

  private scheduleMandatoryRecalc(): void {
    this.queueMandatoryApply();
    if (this.mandatoryTimeout) {
      clearTimeout(this.mandatoryTimeout);
    }
    // axProduct targeting often arrives after the first paint; re-check shortly.
    this.mandatoryTimeout = setTimeout(() => {
      this.mandatoryTimeout = null;
      this.queueMandatoryApply();
    }, 2000);
  }

  private queueMandatoryApply(): void {
    deferSignalStateWrite(() => {
      const next = TargetingHelper.getTargetsWithTargetsDirectly(_.get(this.model, ['axProduct', 'targeting'])) || {};
      if (_.isEqual(next, this.mandatoryElements)) {
        return;
      }
      this.mandatoryElements = next;
      const activeTargets = this.activeTargets();
      _.forEach(activeTargets, (target) => {
        target.isMandatory = !!this.mandatoryElements[target.id];
      });
      this.activeTargets.set([...activeTargets]);
    });
  }

  private setupCopyHook(): void {
    if (!this.copyHook || typeof this.copyHook !== 'object') {
      return;
    }

    this.copyHook.results = (target: ActiveTarget, results: boolean) => {
      target.results = results;
      this.activeTargets.set([...this.activeTargets()]);
    };
    this.copyHook.clear = () => {
      const activeTargets = this.activeTargets();
      _.forEach(activeTargets, (target) => {
        target.results = false;
      });
      this.activeTargets.set([...activeTargets]);
    };
    this.copyHook.clear();
  }
}
